/**
 * Certifications — admin staff qualification + expiry tracking.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { AdminHeader } from "./_shared";

const STATUS_PALETTE = {
  valid: "bg-[#E6EFE8] text-[#1F3A2A]",
  expiring_soon: "bg-[#FDF1D2] text-[#8C6D1F]",
  expired: "bg-[#FCEEEE] text-[#A11D1D]",
};

export default function Certifications() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, u] = await Promise.all([
        api.get("/admin/ops/certifications").catch(() => ({ data: [] })),
        api.get("/admin/staff").catch(() => ({ data: [] })),
      ]);
      setItems(c.data || []);
      setStaff((u.data || []).filter((x) => x.role !== "parent"));
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (form) => {
    try {
      await api.post("/admin/ops/certifications", {
        staff_user_id: form.staff_user_id,
        cert_type: form.cert_type,
        cert_authority: form.cert_authority || null,
        issued_at: form.issued_at || null,
        expires_at: form.expires_at || null,
      });
      toast.success("Certification recorded.");
      setModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ops.errors.generic"));
    }
  };
  const del = async (id) => {
    if (!window.confirm("Remove this certification?")) return;
    try {
      await api.delete(`/admin/ops/certifications/${id}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ops.errors.generic"));
    }
  };

  const expiringCount = items.filter((c) => c.status === "expiring_soon" || c.status === "expired").length;

  return (
    <main className="p-6 lg:p-10 space-y-6" data-testid="admin-certifications">
      <AdminHeader
        eyebrow={t("ops.certs.eyebrow")}
        title={t("ops.certs.title")}
        action={
          <button
            onClick={() => setModal({
              staff_user_id: staff[0]?.id || "",
              cert_type: "",
              cert_authority: "",
              issued_at: "",
              expires_at: "",
            })}
            data-testid="cert-create-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[#1F3A2A] text-white text-sm font-medium px-4 py-2 hover:bg-[#132C17]"
          >
            <Plus size={14} strokeWidth={2} />
            {t("ops.certs.create_cta")}
          </button>
        }
      />
      <p className="text-sm text-[#6B7280] max-w-2xl">{t("ops.certs.blurb")}</p>

      {expiringCount > 0 && (
        <div data-testid="certs-expiring-banner" className="rounded-2xl border border-[#F4E5C5] bg-[#FDF1D2] p-4 flex items-center gap-3">
          <AlertTriangle size={18} strokeWidth={1.6} className="text-[#8C6D1F] shrink-0" />
          <p className="text-sm text-[#8C4A1F]">
            {expiringCount} certification{expiringCount !== 1 ? "s" : ""} need attention.
          </p>
        </div>
      )}

      <section className="rounded-3xl bg-white border border-[#EEF0F4] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-[#6B7280] uppercase tracking-[0.1em] text-[10px] bg-[#FAFAFB]">
              <tr>
                <th className="px-5 py-3">{t("ops.certs.table_staff")}</th>
                <th className="px-5 py-3">{t("ops.certs.table_type")}</th>
                <th className="px-5 py-3">{t("ops.certs.table_authority")}</th>
                <th className="px-5 py-3">{t("ops.certs.table_expires")}</th>
                <th className="px-5 py-3">{t("ops.certs.table_status")}</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="py-10 text-center font-serif italic text-[#9CA3AF]">{t("ops.common.loading")}</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={6} className="py-10 text-center font-serif italic text-[#9CA3AF]" data-testid="certs-empty">{t("ops.certs.no_certs")}</td></tr>
              ) : (
                items.map((c) => (
                  <tr key={c.id} className="border-t border-[#EEF0F4]" data-testid={`cert-row-${c.id}`}>
                    <td className="px-5 py-3 font-medium text-[#1A1B2E]">{c.staff_name}</td>
                    <td className="px-5 py-3 text-[#1A1B2E]">{c.cert_type}</td>
                    <td className="px-5 py-3 text-[#6B7280]">{c.cert_authority || "—"}</td>
                    <td className="px-5 py-3 text-[#6B7280] tabular-nums">{c.expires_at || "—"}</td>
                    <td className="px-5 py-3">
                      <span className={`text-[10px] tracking-[0.1em] uppercase font-semibold rounded-full px-2 py-0.5 ${STATUS_PALETTE[c.status]}`}>
                        {t(`ops.certs.status_${c.status}`)}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => del(c.id)} data-testid={`cert-delete-${c.id}`} className="text-[#A11D1D] hover:underline">
                        <Trash2 size={14} strokeWidth={1.6} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {modal && (
        <div role="dialog" aria-modal="true" data-testid="cert-modal" onClick={() => setModal(null)} className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl text-[#1A1B2E]">{t("ops.certs.create_cta")}</h3>
            <div className="mt-5 space-y-3">
              <label className="block">
                <span className="eyebrow">{t("ops.certs.form_staff")}</span>
                <select value={modal.staff_user_id} onChange={(e) => setModal({ ...modal, staff_user_id: e.target.value })} data-testid="cert-form-staff" className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm bg-white">
                  {staff.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              </label>
              <label className="block">
                <span className="eyebrow">{t("ops.certs.form_type")}</span>
                <input type="text" value={modal.cert_type} onChange={(e) => setModal({ ...modal, cert_type: e.target.value })} data-testid="cert-form-type" className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm" />
              </label>
              <label className="block">
                <span className="eyebrow">{t("ops.certs.form_authority")}</span>
                <input type="text" value={modal.cert_authority} onChange={(e) => setModal({ ...modal, cert_authority: e.target.value })} data-testid="cert-form-authority" className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm" />
              </label>
              <label className="block">
                <span className="eyebrow">{t("ops.certs.form_issued_at")}</span>
                <input type="date" value={modal.issued_at} onChange={(e) => setModal({ ...modal, issued_at: e.target.value })} data-testid="cert-form-issued" className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm" />
              </label>
              <label className="block">
                <span className="eyebrow">{t("ops.certs.form_expires_at")}</span>
                <input type="date" value={modal.expires_at} onChange={(e) => setModal({ ...modal, expires_at: e.target.value })} data-testid="cert-form-expires" className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm" />
              </label>
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button onClick={() => setModal(null)} className="text-sm text-[#6B7280] hover:text-[#1A1B2E]">{t("ops.common.cancel")}</button>
              <button onClick={() => submit(modal)} data-testid="cert-form-submit" className="inline-flex items-center gap-2 rounded-full bg-[#1F3A2A] text-white text-sm font-medium px-4 py-2 hover:bg-[#132C17]">{t("ops.certs.submit_create")}</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
